import { ApolloError } from '@apollo/client';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import { TypeOptions } from 'react-toastify/dist/types';

import './toast-styles.css';
import { ReturnComponent } from './utils/reactTypes';

//////////////////////////////////////////////////////////////////////////////
// Internal helpers.
//////////////////////////////////////////////////////////////////////////////

// Q: How do we deduplicate toasts?
//
// A: We use the `toastId` option of react-toastify.
//
// react-toastify won’t display a toast if a toast with the same ID has already
//  being displayed (see https://fkhadra.github.io/react-toastify/use-a-custom-id).
//
// By making the message the toast ID, the same message will never be displayed
//  in multiple toasts at the same time.
//
// Toast with abandon, lads!
//
/*
 _..--""````""--.._
(_                _;..--""````""--.._
\ ```"""----"""```(_                _)
 '-.            .-\ ```"""----"""``` /
    `\        /`   '-.            .-'
      '-.__.-'        `\        /`
         ||             '-.__.-'
         ||                ||
         ||                ||
         ||                ||
    _..--||--.._           ||
   (_          _)     _..--||--.._
     ```""""```      (_          _)
                       ```""""```
*/

type AxleToastOptions = {
    deduplicate?: boolean;
    autoClose?: number | false | undefined;
};

const genericToast = (
    message: string,
    type: TypeOptions,
    { deduplicate = true, autoClose }: AxleToastOptions = {},
) => {
    toast(message, {
        type,
        autoClose,
        ...(deduplicate && { toastId: [type, message].join(',') }),
    });
};

//////////////////////////////////////////////////////////////////////////////
// Public functions for toasting.
//////////////////////////////////////////////////////////////////////////////

export const successToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.SUCCESS, options);
};

export const warningToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.WARNING, options);
};

export const errorToast = (message: string, options: AxleToastOptions = {}) => {
    genericToast(message, toast.TYPE.ERROR, options);
};

export const onError = (error: ApolloError) => {
    errorToast(error.message);
};

//////////////////////////////////////////////////////////////////////////////
// Public toast container.
//////////////////////////////////////////////////////////////////////////////

export const AxleToastContainer = (): ReturnComponent => (
    <ToastContainer
        // Container options:
        limit={3} // Only show at most 3 toasts at a time. The rest will wait their turn.
        newestOnTop={false}
        rtl={false}
        // Defaults for toast options.
        position={toast.POSITION.BOTTOM_CENTER}
        autoClose={3000} // Stay on screen for 3 seconds.
        hideProgressBar // Otherwise, shows a countdown timer progress bar; kinda stressful and distracting.
        closeOnClick={true} // Allow clicking the toast to dismiss it.
        draggable // Allow dragging the toast to dismiss it.
        draggablePercent={60} // Make dragging to dismiss easier.
        draggableDirection={'y'} // Make it vertical dragging instead of horizontal.
        pauseOnFocusLoss={true} // Stop the countdown timer if the window loses focus.
        pauseOnHover={true} // Stop the countdown timer if the cursor is on the toast.
        // The rest of the styling is in `src/toast-styles.css`.
        style={{ maxWidth: '80vw', width: '25rem' }}
    />
);
